import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';

export const SQLITE_FILE_NAME = 'workschedule.sqlite';

export type SqliteBytes = Uint8Array;

type Persistence = {
  read: () => Promise<SqliteBytes | null>;
  write: (bytes: SqliteBytes) => Promise<void>;
};

const IDB_NAME = 'workschedule';
const IDB_STORE = 'sqlite';
const IDB_KEY = 'db';

const toUint8Array = (data: Uint8Array | ArrayBuffer | number[]): SqliteBytes => {
  if (data instanceof Uint8Array) return data;
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  return Uint8Array.from(data);
};

const uint8ToBase64 = (bytes: SqliteBytes): string => {
  let binary = '';
  const chunkSize = 0x8000;

  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(
      ...bytes.subarray(offset, offset + chunkSize),
    );
  }

  return btoa(binary);
};

const base64ToUint8 = (value: string): SqliteBytes => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
};

const openIndexedDb = () =>
  new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(IDB_NAME, 1);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(IDB_STORE)) {
        database.createObjectStore(IDB_STORE);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const capacitorPersistence: Persistence = {
  read: async () => {
    try {
      const result = await Filesystem.readFile({
        path: SQLITE_FILE_NAME,
        directory: Directory.Documents,
      });

      if (typeof result.data !== 'string') {
        const buffer = await result.data.arrayBuffer();
        return new Uint8Array(buffer);
      }

      return base64ToUint8(result.data);
    } catch {
      return null;
    }
  },
  write: async (bytes) => {
    await Filesystem.writeFile({
      path: SQLITE_FILE_NAME,
      data: uint8ToBase64(bytes),
      directory: Directory.Documents,
      recursive: true,
    });
  },
};

const indexedDbPersistence: Persistence = {
  read: async () => {
    const database = await openIndexedDb();

    try {
      return await new Promise<SqliteBytes | null>((resolve, reject) => {
        const request = database
          .transaction(IDB_STORE, 'readonly')
          .objectStore(IDB_STORE)
          .get(IDB_KEY);

        request.onsuccess = () => {
          resolve(request.result ? toUint8Array(request.result) : null);
        };
        request.onerror = () => reject(request.error);
      });
    } finally {
      database.close();
    }
  },
  write: async (bytes) => {
    const database = await openIndexedDb();

    try {
      await new Promise<void>((resolve, reject) => {
        const transaction = database.transaction(IDB_STORE, 'readwrite');
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
        transaction.objectStore(IDB_STORE).put(bytes, IDB_KEY);
      });
    } finally {
      database.close();
    }
  },
};

const HTTP_STORAGE_HEADER = 'x-work-schedule-storage';

const httpPersistence: Persistence = {
  read: async () => {
    const response = await fetch('/api/db', { cache: 'no-store' });

    if (response.status === 204) return null;
    if (!response.ok) {
      throw new Error(`Failed to read schedule database (${response.status})`);
    }

    return toUint8Array(await response.arrayBuffer());
  },
  write: async (bytes) => {
    const response = await fetch('/api/db', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: new Blob([bytes]),
    });

    if (!response.ok) {
      throw new Error(`Failed to save schedule database (${response.status})`);
    }
  },
};

let webPersistence: Persistence | undefined;

export const isHttpStorageAvailable = async () => {
  try {
    const response = await fetch('/api/auth/status', {
      method: 'GET',
      cache: 'no-store',
    });
    return response.ok && response.headers.get(HTTP_STORAGE_HEADER) === 'sqlite';
  } catch {
    return false;
  }
};

const resolveWebPersistence = async (): Promise<Persistence> => {
  if (!webPersistence) {
    webPersistence = (await isHttpStorageAvailable())
      ? httpPersistence
      : indexedDbPersistence;
  }

  return webPersistence;
};

const browserPersistence: Persistence = {
  read: async () => (await resolveWebPersistence()).read(),
  write: async (bytes) => (await resolveWebPersistence()).write(bytes),
};

export const getPersistence = (): Persistence => {
  const sqliteFile = window.electron?.sqliteFile;
  if (sqliteFile) {
    return {
      read: async () => {
        const data = await sqliteFile.read();
        return data ? toUint8Array(data) : null;
      },
      write: (bytes) => sqliteFile.write(bytes),
    };
  }

  if (Capacitor.isNativePlatform()) {
    return capacitorPersistence;
  }

  return browserPersistence;
};
